// Katalog baris metrik Summary Compare: ubah body rollup jadi baris tabel flat.
//
// `order` nyimpen level grup + metrik (groupIndex*100000 + metricIndex*1000), tapi
// `metricIndex` juga diekspos apa adanya di tiap baris -- `mergeRows` gak boleh
// nebak grup/metrik dari hasil bagi `order`, field eksplisit lebih aman.
// Channel/arah/subgroup TIDAK ikut dikompres ke `order` -- katalog diproses satu
// sisi per panggilan, jadi penomoran posisi channel bisa beda antar sisi kiri/kanan
// dan bikin baris tabrakan atau ketuker pas di-merge. Urutan channel (dan, buat
// Flowmeter, sub-bagian per channel) lahir belakangan di `mergeRows`
// (summaryCompareModel.js) lewat subgroupRank + subgroup + channelRank + channelName + direction.
import { metersToNm, kmhToKnot } from '../units.js';
import { tempAverageC } from '../temperature.js';

export const GROUPS = [
  { key: 'gps', label: 'GPS' },
  { key: 'rpm', label: 'Main engine (RPM)' },
  { key: 'ae', label: 'AE' },
  { key: 'flowmeter', label: 'Flowmeter' },
  { key: 'temperature', label: 'Temperature' },
];

const GROUP_INDEX = {};
GROUPS.forEach((g, i) => { GROUP_INDEX[g.key] = i; });

// Konstanta, bukan hasil hitungan posisi -- nama yang sama selalu dapat rank yang
// sama di kiri maupun kanan.
function channelRankOf(name) {
  if (name === 'PORT') return 0;
  if (name === 'STARBOARD') return 1;
  if (name === 'CENTER') return 2;
  return 3;
}

// channelRankOf gak pernah lebih dari 3, jadi sub-bagian "Total" (ME consumption)
// selalu jatuh sesudah semua sub-bagian channel apa pun nama channel-nya.
const TOTAL_SUBGROUP_RANK = 4;

// null/undefined/string kosong (atau cuma spasi) -> null duluan, sebelum masuk konversi apapun.
// Number('') dan Number(null) itu 0 dan finite, jadi harus disaring manual di sini.
function toValue(raw, convert) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' && raw.trim() === '') return null;
  const num = convert ? convert(raw) : Number(raw);
  return Number.isFinite(num) ? num : null;
}

function titleCase(text) {
  return String(text || '')
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function makeRow(
  key, group, metricIndex, label, unit, format, measure, value,
  channelName = '', direction = 0, subgroup = '', subgroupRank = 0,
) {
  return {
    key,
    group,
    label,
    unit,
    format,
    measure,
    order: GROUP_INDEX[group] * 100000 + metricIndex * 1000,
    metricIndex,
    channelRank: channelRankOf(channelName),
    channelName,
    direction,
    subgroup,
    subgroupRank,
    value,
  };
}

function gpsRows(body) {
  const summary = (body && body.summary) || {};
  const rows = [];
  rows.push(makeRow('gps.distance', 'gps', 0, 'Distance', 'NM', 'number', 'total', toValue(summary.totalDistance, metersToNm)));
  rows.push(makeRow('gps.speed', 'gps', 1, 'Avg speed', 'kn', 'number', 'average', toValue(summary.averageSpeed, kmhToKnot)));
  return rows;
}

function rpmRows(body) {
  const summary = (body && body.summary) || {};
  const average = summary.average || {};
  const runningTime = summary.runningTime || {};
  const channels = Array.from(new Set([...Object.keys(average), ...Object.keys(runningTime)]));
  const rows = [];
  channels.forEach((ch) => {
    rows.push(makeRow(`rpm.avg.${ch}`, 'rpm', 0, `Avg RPM ${ch}`, 'RPM', 'number', 'average', toValue(average[ch]), ch));
    rows.push(makeRow(`rpm.running.${ch}`, 'rpm', 1, `Running time ${ch}`, '', 'duration', 'total', toValue(runningTime[ch]), ch));
  });
  return rows;
}

function aeRows(body) {
  const summary = (body && body.summary) || {};
  const fuel = summary.fuelConsumption || {};
  const running = summary.runningSeconds || {};
  const names = Array.from(new Set([...Object.keys(fuel), ...Object.keys(running)]));
  const rows = [];
  let fuelTotal = null;
  let runningTotal = null;
  names.forEach((name) => {
    const fuelVal = toValue(fuel[name]);
    const runningVal = toValue(running[name]);
    if (fuelVal !== null) fuelTotal = (fuelTotal || 0) + fuelVal;
    if (runningVal !== null) runningTotal = (runningTotal || 0) + runningVal;
    rows.push(makeRow(`ae.fuel.${name}`, 'ae', 0, `Fuel ${name}`, 'L', 'number', 'total', fuelVal, name));
    rows.push(makeRow(`ae.running.${name}`, 'ae', 2, `Running time ${name}`, '', 'duration', 'total', runningVal, name));
  });
  // Slot metrik sendiri, nempel di akhir bloknya masing-masing: fuel=0 lalu
  // fuelTotal=1, running=2 lalu runningTotal=3. Tetap gak ada tabrakan `order`
  // kalau dua sisi punya jumlah AE beda -- yang beda cuma posisi total, nempel
  // ke bloknya sendiri bukan numpuk semua di paling bawah.
  rows.push(makeRow('ae.fuelTotal', 'ae', 1, 'Total fuel', 'L', 'number', 'total', fuelTotal));
  rows.push(makeRow('ae.runningTotal', 'ae', 3, 'Total running time', '', 'duration', 'total', runningTotal));
  return rows;
}

// key flowmeter turunan (portAverageFlow, portEngineCons, dst) diabaikan; cuma _IN/_OUT & meEngineCons.
const FLOW_CHANNEL_RE = /^(.+)_(IN|OUT)$/;

function flowmeterRows(body) {
  const summary = (body && body.summary) || {};
  const channels = [];
  Object.keys(summary).forEach((key) => {
    const match = FLOW_CHANNEL_RE.exec(key);
    const val = summary[key];
    if (match && val && typeof val === 'object') {
      channels.push({ base: match[1], direction: match[2], data: val });
    }
  });
  const rows = [];
  channels.forEach((c) => {
    const dirIdx = c.direction === 'OUT' ? 1 : 0;
    const dirLabel = c.direction === 'IN' ? 'in' : 'out';
    // Sub-bagian per channel: nama channel dibuang dari label (jadi sub-judul),
    // subgroup + subgroupRank yang nentuin channel-nya duluan sebelum jenis metrik.
    const subgroupRank = channelRankOf(c.base);
    rows.push(makeRow(`fm.volume.${c.base}.${dirLabel}`, 'flowmeter', 0, `Volume ${dirLabel}`, 'L', 'number', 'total', toValue(c.data._totalVolumeFlow), c.base, dirIdx, c.base, subgroupRank));
    rows.push(makeRow(`fm.flowrate.${c.base}.${dirLabel}`, 'flowmeter', 1, `Avg flowrate ${dirLabel}`, 'L/h', 'number', 'average', toValue(c.data._averageFlowrate), c.base, dirIdx, c.base, subgroupRank));
    rows.push(makeRow(`fm.density.${c.base}.${dirLabel}`, 'flowmeter', 2, `Avg density ${dirLabel}`, 'g/cm³', 'number', 'average', toValue(c.data._averageDens), c.base, dirIdx, c.base, subgroupRank));
  });
  // Sub-bagian "Total" -- selalu paling akhir (subgroupRank di atas rank channel
  // mana pun), apa pun nama/jumlah channel flowmeter-nya.
  rows.push(makeRow('fm.meCons', 'flowmeter', 0, 'ME consumption', 'L', 'number', 'total', toValue(summary.meEngineCons), '', 0, 'Total', TOTAL_SUBGROUP_RANK));
  return rows;
}

function tempGroupLabel(group) {
  if (group === 'EXHAUST_TEMPERATURE') return 'Exhaust temp';
  if (group === 'TEMPERATURE') return 'Tank temp';
  return titleCase(group);
}

function temperatureRows(body) {
  const summary = (body && body.summary) || {};
  const tempDetails = summary.tempDetails || {};
  // Alfabetis biar urutan grup gak gantung ke urutan key respons.
  const groupNames = Object.keys(tempDetails).slice().sort();
  const rows = [];
  groupNames.forEach((groupName, gi) => {
    const positions = tempDetails[groupName] || {};
    Object.keys(positions).forEach((pos) => {
      const entries = positions[pos] || [];
      const value = tempAverageC(entries[0]);
      rows.push(makeRow(`temp.${groupName}.${pos}`, 'temperature', gi, `${tempGroupLabel(groupName)} ${pos}`, '°C', 'number', 'average', value, pos));
    });
  });
  return rows;
}

const BUILDERS = {
  gps: gpsRows,
  rpm: rpmRows,
  ae: aeRows,
  flowmeter: flowmeterRows,
  temperature: temperatureRows,
};

export function rowsFromSummary(body, unavailable) {
  const unavailableList = unavailable || [];
  const notServed = [];
  const rows = [];
  GROUPS.forEach((group) => {
    if (unavailableList.includes(group.key)) {
      notServed.push(group.key);
      return;
    }
    const groupBody = (body && body[group.key]) || null;
    if (!groupBody) return;
    rows.push(...BUILDERS[group.key](groupBody));
  });
  return { rows, notServed };
}
